import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { authSelector } from '@core/services/auth/state/authentication.selectors';
import { select, Store } from '@ngrx/store';
import { ROUTE_ANIMATIONS_ELEMENTS } from '@shared/services/animations/route.animations';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { map, tap, startWith, debounceTime, distinctUntilChanged, switchMap, shareReplay, takeUntil } from 'rxjs/operators';
import { NgxNotifyService } from '@shared/services/ngx-notify/ngx-notify.service';

import { ProtocolModel } from '../models/protocol.model';
import { protocolById, protocolUpdate } from '../store/protocol.actions';
import { ProtocolState } from '../store/protocol.reducers';
import { protocolSelector } from './../store/protocol.selectors';
import { OptionModel } from '@features/admin/option-management/models/option.model';
import { OptionType } from '@features/admin/option-management/models/option-type.enum';
import { optionSelector } from '@features/admin/option-management/store/option.selectors';
import { SubCategoryModel } from '../../admin/option-management/models/sub-category.model';
import { CategoryModel } from '../../admin/option-management/models/category.model';
import { ResearcherService } from '@features/admin/researcher-management/services/researcher.service';
import { Researcher } from '@features/admin/researcher-management/models/researcher.model';
import { displayResearcher as displayResearcherUtil, formatResearcher as formatResearcherUtil, formatPhone as formatPhoneUtil } from '@shared/researcher-format.util';
@Component({
  selector: 'app-protocol-edit',
  templateUrl: './protocol-edit.component.html',
  styleUrls: ['./protocol-edit.component.scss']
})
export class ProtocolEditComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private formDestroy$ = new Subject<void>();
  private requestedProtocolId = 0;
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;
  data$!: Observable<ProtocolModel | null>;
  loading = true;
  loadError: string | null = null;
  formError: string | null = null;
  form: FormGroup = this.fb.group({});
  model = {} as ProtocolModel;
  isAdmin$ = this.store.pipe(select(authSelector.isAdmin));

  // Dropdown options
  statusOptions$!: Observable<OptionModel[]>;

  // Autocomplete observables
  filteredPrincipalInvestigators$!: Observable<Researcher[]>;
  filteredNtrirbCoordinators$!: Observable<Researcher[]>;

  researchTypeOptions$!: Observable<OptionModel[]>;
  departmentOptions$!: Observable<OptionModel[]>;
  schoolOptions$!: Observable<OptionModel[]>;
  institutionOptions$!: Observable<OptionModel[]>;
  collaboratingInstitutionOptions$!: Observable<OptionModel[]>;
  sponsorTypeOptions$!: Observable<CategoryModel[]>;
  reviewTypeOptions$!: Observable<OptionModel[]>;
  reviewCycleOptions$!: Observable<OptionModel[]>;
  specialFindingOptions$!: Observable<any[]>;
  fundingAgency$!: Observable<OptionModel[]>;
  natureOfResearch$!: Observable<OptionModel[]>;
  sponsoredClinicalTrial$!: Observable<OptionModel[]>;
  irbofRecord$!: Observable<any[]>;
  cededReviewTypeId?: number;
  sponsoredResearchTypeId?: number;
  formReady = false;

  filteredSponsors: { [index: number]: SubCategoryModel[] } = {};
  allSubCategories: SubCategoryModel[] = [];
  personnelInstitutionOptions$!: Observable<OptionModel[]>;
  availableRoles$!: Observable<OptionModel[]>;
  subs$: Subscription[] = [];
  searchResearcher: { [index: number]: Researcher[] } = {};

  constructor(
    private fb: FormBuilder,
    private store: Store<ProtocolState>,
    private route: ActivatedRoute,
    private researcherService: ResearcherService,
    private notify: NgxNotifyService
  ) { }

  ngOnInit(): void {
    this.data$ = this.getSelectedProtocol();
    this.store.pipe(select(optionSelector.sponsorSubCategories), takeUntil(this.destroy$)).subscribe((subs: SubCategoryModel[]) => {
      this.allSubCategories = subs;
    });
  }

  ngOnDestroy(): void {
    this.formDestroy$.next();
    this.formDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
    this.subs$.forEach(sub => sub.unsubscribe());
  }

  public displayResearcher = (r: Researcher | null) => displayResearcherUtil(r);
  public formatResearcher = (r: Researcher) => formatResearcherUtil(r);
  public formatPhone = (p: string) => formatPhoneUtil(p);

  save(): void {
    if (!this.formReady || this.loading || this.loadError || this.formError || this.model.id !== this.requestedProtocolId) {
      this.notify.error('The selected protocol is not ready to save. Please reload it.');
      return;
    }
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();

    const sponsorTypeCategories = (raw.sponsorTypeCategories || []).filter((entry: any) =>
      typeof entry.categoryId === 'number' &&
      typeof entry.subCategoryId === 'number'
    );

    const personnelList = raw.personnelList.map((entry: any) => ({
      researcherId: entry.researcherId.id,
      rolesId: entry.rolesId,
      institutionId: entry.institutionId,
      startDate: entry.startDate,
      endDate: entry.endDate
    }));

    // Remove search fields before saving
    delete raw.investigatorSearch;
    delete raw.ntrIrbCoordinatorSearch;

    const updated = {
      ...raw,
      sponsorTypeCategories,
      id: this.model.id,
      personnelList
    };

    this.form.markAsPristine();
    this.store.dispatch(protocolUpdate({ data: { protocol: updated } }));
  }

  cancel(): void {
    window.history.back();
  }

  retry(): void {
    this.formDestroy$.next();
    this.formReady = false;
    this.formError = null;
    this.store.dispatch(protocolById({ id: this.requestedProtocolId }));
  }

  private getSelectedProtocol(): Observable<ProtocolModel | null> {
    return this.route.params.pipe(
      map(params => Number(params.id)),
      distinctUntilChanged(),
      switchMap(id => {
        this.requestedProtocolId = id;
        this.formDestroy$.next();
        this.formReady = false;
        this.formError = null;
        this.loading = true;
        this.loadError = null;
        if (!Number.isSafeInteger(id) || id <= 0) {
          this.loading = false;
          this.loadError = 'The selected protocol ID is invalid.';
          return of(null);
        }
        this.store.dispatch(protocolById({ id }));
        let initialized: ProtocolModel | null = null;
        return this.store.pipe(
          select(protocolSelector.allProtocols),
          map(state => {
            this.loading = state.requestedProtocolId === id && state.loading;
            this.loadError = state.requestedProtocolId === id ? state.error : null;
            const protocol = state.selectedProtocol?.id === id && !this.loading && !this.loadError
              ? state.selectedProtocol : null;
            if (!protocol) {
              initialized = null;
              this.formReady = false;
            } else if (protocol !== initialized) {
              initialized = protocol;
              this.model = { ...protocol };
              this.initForm(protocol);
              this.initDropdowns(protocol);
            }
            return protocol;
          })
        );
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private initForm(model: ProtocolModel): void {
    this.formDestroy$.next();
    this.subs$.forEach(sub => sub.unsubscribe());
    this.subs$ = [];
    this.formReady = false;
    this.formError = null;
    this.filteredSponsors = {};
    this.searchResearcher = {};

    this.sponsorTypeCategoriesForm?.clear(); // clear previous form array if it exists
    const sponsorTypeArray = this.fb.array([]);
    model.sponsorTypeCategories?.forEach((entry, i) => {
      sponsorTypeArray.push(
        this.fb.group({
          categoryId: [entry.categoryId ?? null, Validators.required],
          subCategoryId: [entry.subCategoryId ?? null, Validators.required],
          sponsorProtocol: [entry.sponsorProtocol ?? '', Validators.maxLength(250)],
        })
      );
    });
    const personnelArray = this.fb.array([]);

    this.form = this.fb.group({
      studyTitle: [model.studyTitle || '', Validators.required],
      irbNetNumber: [model.irbNetNumber || '', Validators.maxLength(250)],
      irbNumber: [model.irbNumber || ''], //hidden field required for backend
      protocolStatusId: [model.protocolStatusId || null],
      previousIrbNumber: [model.previousIrbNumber || ''],
      principalInvestigatorId: [model.principalInvestigatorId || null, Validators.required],
      investigatorSearch: ['', Validators.required], // For autocomplete
      ntrIrb_CoordinatorId: [model.ntrIrb_CoordinatorId || null],
      ntrIrbCoordinatorSearch: [''], // For autocomplete
      coInvestigator: [model.coInvestigator || ''],
      crCoordinator: [model.crCoordinator || ''],
      typeOfResearchId: [model.typeOfResearchId || null],
      departmentId: [model.departmentId || null],
      schoolId: [model.schoolId || null],
      institutionId: [model.institutionId || null],
      collaboratingInstitutionId: [model.collaboratingInstitutionId || null],
      sponsorTypeId: [model.sponsorTypeId || null],
      sponsor: [model.sponsor || '', Validators.maxLength(250)],
      sponsorProtocol: [model.sponsorProtocol || '', Validators.maxLength(250)],
      sponsorTypeCategories: sponsorTypeArray,
      typeOfReviewId: [model.typeOfReviewId || null],
      reviewCycleId: [model.reviewCycleId || null],
      specialFindingCategories: [model.specialFindingCategories || []],
      specialFindings: [model.specialFindings || ''],
      submissionDate: [model.submissionDate || null, Validators.required],
      initialReview: [model.initialReview || null],
      mostRecentReview: [model.mostRecentReview || null],
      initialApprovalDate: [model.initialApprovalDate || null],
      approvalThrough: [model.approvalThrough || null],
      sendNextReview: [model.sendNextReview || null],
      continuingReviewDate: [model.continuingReviewDate || null],
      completeDate: [model.completeDate || null],
      irbofRecordId: [model.irbofRecordId || null],
      fundingAgencyId: [model.fundingAgencyId || null],
      natureOfResearchId: [model.natureOfResearchId || null],
      sponsoredClinicalTrialId: [model.sponsoredClinicalTrialId || null],
      personnelList: personnelArray
    });

    // Setup autocomplete streams
    this.filteredPrincipalInvestigators$ = this.form.get('investigatorSearch')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value === 'string' && value.trim()) {
          return this.researcherService.searchResearchers(value, 'PrincipalInvestigator',true);
        }
        return of([]);
      })
    );

    this.filteredNtrirbCoordinators$ = this.form.get('ntrIrbCoordinatorSearch')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value === 'string' && value.trim()) {
          return this.researcherService.searchResearchers(value, 'NTRIRBCoordinator', true);
        }
        return of([]);
      })
    );

    // Populate filteredSponsors for each row (used in our select dropdowns)
    model.sponsorTypeCategories?.forEach((entry, i) => {
      this.filteredSponsors[i] = this.allSubCategories.filter(
        s => s.categoryId === entry.categoryId &&
          (s.isActive || s.id === entry.subCategoryId)
      );
    });

    // Push one row on start if empty for consistency
    if (!model.sponsorTypeCategories?.length) {
      this.addSponsorTypeCategory();
    }



    // Keep PI ID in sync with the PI search box (and make it required)
    this.bindAutocompleteToId('investigatorSearch', 'principalInvestigatorId', true);

    // Keep NTR IRB Coordinator ID in sync (optional)
    this.bindAutocompleteToId('ntrIrbCoordinatorSearch', 'ntrIrb_CoordinatorId');

    const investigator = model.principalInvestigatorId ? model.principalInvestigator : null;
    const coordinator = model.ntrIrb_CoordinatorId ? model.ntrIrb_Coordinator : null;
    const missingResearcher =
      (model.principalInvestigatorId && investigator?.id !== model.principalInvestigatorId) ||
      (model.ntrIrb_CoordinatorId && coordinator?.id !== model.ntrIrb_CoordinatorId) ||
      model.personnelList?.some(entry => !entry.researcher || entry.researcher.id !== entry.researcherId);
    if (missingResearcher) {
      this.formError = 'The protocol response is missing selected researcher details. Please try again.';
      this.notify.error(this.formError);
      return;
    }

    this.form.patchValue({ investigatorSearch: investigator, ntrIrbCoordinatorSearch: coordinator }, { emitEvent: false });
    model.personnelList?.forEach((entry, i) => {
      const group = this.newPersonnel();
      group.patchValue({ researcherId: entry.researcher, rolesId: entry.rolesId, institutionId: entry.institutionId,
        startDate: entry.startDate, endDate: entry.endDate }, { emitEvent: false });
      personnelArray.push(group);
      this.ResearcherSearch(i);
    });
    this.form.markAsPristine();
    this.formReady = true;
  }

  clearNtrIrbCoordinator() {
    this.form.patchValue({
      ntrIrbCoordinatorSearch: null,
      ntrIrb_CoordinatorId: null
    }, { emitEvent: false });
    this.form.get('ntrIrb_CoordinatorId')?.markAsDirty();
  }


  private initDropdowns(model: ProtocolModel): void {
    const get = (type: OptionType, selectedId?: number) =>
      this.store.pipe(
        select(optionSelector.optionsByType(type, true)),
        map(options => this.appendSelectedOptionIfDisabled(selectedId ?? 0, options))
      );

    this.statusOptions$ = get(OptionType.protocolStatus, model.protocolStatusId);
    this.departmentOptions$ = get(OptionType.department, model.departmentId);
    this.schoolOptions$ = get(OptionType.school, model.schoolId);
    this.institutionOptions$ = get(OptionType.institution, model.institutionId);
    this.collaboratingInstitutionOptions$ = get(OptionType.collaboratingInstitution, model.collaboratingInstitutionId);
    this.personnelInstitutionOptions$ = get(OptionType.institution, this.model.personnelList?.find(p => p.protocolId === this.model.id)?.institutionId); // Get institutionId from first personnel entry if exists
    const rolesIds = this.model.personnelList?.find(p => p.protocolId === this.model.id)?.rolesId || [];

    this.availableRoles$ = this.store.pipe(
      select(optionSelector.roles),
      map(options => {
        const active = options.filter((o: any) => o.isActive);
        const inactive = options.filter((o: any) =>
          rolesIds?.includes(o.id) && !o.isActive
        );
        return [...active, ...inactive].sort((a, b) => a.name.localeCompare(b.name));
      })
    );

    const usedCategoryIds = new Set<number>(
      (model.sponsorTypeCategories || []).map(entry => entry.categoryId)
    );

    this.sponsorTypeOptions$ = this.store.pipe(
      select(optionSelector.categories),
      map(categories => categories
        .filter(cat => cat.type?.toLowerCase() === 'sponsor')
        .filter(cat => cat.isActive || usedCategoryIds.has(cat.id))
      )
    );

    this.fundingAgency$ = get(OptionType.fundingAgency, this.model.fundingAgencyId);
    this.natureOfResearch$ = get(OptionType.natureOfResearch, this.model.natureOfResearchId);
    this.sponsoredClinicalTrial$ = get(OptionType.sponsoredClinicalTrial, this.model.sponsoredClinicalTrialId);
    this.irbofRecord$ = get(OptionType.irbofRecord, this.model.irbofRecordId);
    this.reviewTypeOptions$ = get(OptionType.typeOfReview, model.typeOfReviewId).pipe(
      tap(options => {
        const ceded = options.find(o => o.name.toLowerCase() === 'ceded');
        this.cededReviewTypeId = ceded?.id;
        this.setupIrbofRecordValidationWatcher();
      })
    );

    this.researchTypeOptions$ = get(OptionType.typeOfResearch, this.model.typeOfResearchId).pipe(
      tap(options => {
        const typeOfResearch = options.find(o => o.name.toLowerCase() === 'sponsored clinical trial (drug/device/biologic)');
        this.sponsoredResearchTypeId = typeOfResearch?.id;
        this.setupsponsoredClinicalTrialValidationWatcher();
      })
    );

    this.reviewCycleOptions$ = get(OptionType.reviewCycle, model.reviewCycleId);

    this.specialFindingOptions$ = this.store.pipe(
      select(optionSelector.specialFindings),
      map(options => {
        const active = options.filter(o => o.isActive);
        const inactive = options.filter(o =>
          model.specialFindingCategories?.includes(o.id) && !o.isActive
        );
        return [...active, ...inactive].sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }

  // Select researcher for Research Personnel auto complete
  getResearchPersonnel(entry: Researcher): Observable<Researcher> {
    if (!entry || typeof entry === 'string' || !entry.id) {
      return of({} as Researcher);
    }
    return this.researcherService.getById(entry.id);
  }

  // When researcher is selected from autocomplete
  onPrincipalInvestigatorSelected(researcher: Researcher): void {
    this.form.patchValue({ principalInvestigatorId: researcher.id });
    this.form.markAsDirty();
  }

  onNtrirbCoordinatorSelected(researcher: Researcher): void {
    this.form.patchValue({ ntrIrb_CoordinatorId: researcher.id });
    this.form.markAsDirty();
  }


  private appendSelectedOptionIfDisabled(id: number, options: OptionModel[]): OptionModel[] {
    const selectedOption = options.find(x => x.id === id);
    const activeOptions = options.filter(x => x.isActive === true);
    if (selectedOption && !selectedOption.isActive) {
      activeOptions.push(selectedOption);
    }
    return activeOptions.sort((a, b) => a.name.localeCompare(b.name));
  }

  private bindAutocompleteToId(searchCtrlName: string, idCtrlName: string, required = false) {
    const searchCtrl = this.form.get(searchCtrlName);
    const idCtrl = this.form.get(idCtrlName);

    if (!searchCtrl || !idCtrl) return;

    this.subs$.push(
      searchCtrl.valueChanges
        .pipe(debounceTime(150), distinctUntilChanged(), takeUntil(this.formDestroy$))
        .subscribe(val => {
          // If user is typing/cleared text (string/null), clear the id
          if (val == null || typeof val === 'string' || !val.id) {
            // only clear if it was previously set
            if (idCtrl.value !== null) {
              idCtrl.setValue(null, { emitEvent: false });
              idCtrl.markAsDirty();
              idCtrl.updateValueAndValidity({ emitEvent: false });
            }
            return;
          }

          // If the control holds a full object (selected from list), keep id in sync
          if (typeof val === 'object' && val.id) {
            if (idCtrl.value !== val.id) {
              idCtrl.setValue(val.id, { emitEvent: false });
              idCtrl.markAsDirty();
              idCtrl.updateValueAndValidity({ emitEvent: false });
            }
          }
        })
    );

    // Optional: ensure validity if required
    if (required) {
      idCtrl.addValidators(Validators.required);
      idCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }


  dateFields = [
    { label: 'Submission Date', controlName: 'submissionDate' },
    { label: 'Initial Review Date', controlName: 'initialReview' },
    { label: 'Most Recent Review Date', controlName: 'mostRecentReview' },
    { label: 'Initial Approval Date', controlName: 'initialApprovalDate' },
    { label: 'Approval Through Date', controlName: 'approvalThrough' },
    { label: 'Send Next Review Date', controlName: 'sendNextReview' },
    { label: 'Continuing Review Date', controlName: 'continuingReviewDate' },
    { label: 'Complete Date', controlName: 'completeDate' }
  ];

  private setupIrbofRecordValidationWatcher(): void {
    const typeOfReviewControl = this.form.get('typeOfReviewId');
    const irbofRecordControl = this.form.get('irbofRecordId');

    if (!typeOfReviewControl || !irbofRecordControl) return;

    typeOfReviewControl.valueChanges.pipe(takeUntil(this.formDestroy$), takeUntil(this.destroy$)).subscribe((value) => {
      if (value === this.cededReviewTypeId) {
        irbofRecordControl.setValidators([Validators.required]);
      } else {
        irbofRecordControl.clearValidators();
        irbofRecordControl.setValue(null);
      }
      irbofRecordControl.updateValueAndValidity();
    });

    if (typeOfReviewControl.value === this.cededReviewTypeId) {
      irbofRecordControl.setValidators(Validators.required);
      irbofRecordControl.updateValueAndValidity();
    }
  }

  private setupsponsoredClinicalTrialValidationWatcher(): void {
    const typeOfResearchControl = this.form.get('typeOfResearchId');
    const sponsoredClinicalTrialControl = this.form.get('sponsoredClinicalTrialId');

    if (!typeOfResearchControl || !sponsoredClinicalTrialControl) return;

    typeOfResearchControl.valueChanges.pipe(takeUntil(this.formDestroy$), takeUntil(this.destroy$)).subscribe((value) => {
      if (value === this.sponsoredResearchTypeId) {
        sponsoredClinicalTrialControl.setValidators([Validators.required]);
      } else {
        sponsoredClinicalTrialControl.clearValidators();
        sponsoredClinicalTrialControl.setValue(null);
      }
      sponsoredClinicalTrialControl.updateValueAndValidity();
    });

    if (typeOfResearchControl.value === this.sponsoredResearchTypeId) {
      typeOfResearchControl.setValidators([Validators.required]);
      typeOfResearchControl.updateValueAndValidity();
    }
  }

  get sponsorTypeCategoriesForm(): FormArray {
    return this.form.get('sponsorTypeCategories') as FormArray;
  }

  onSponsorTypeChange(index: number): void {
    const group = this.sponsorTypeCategoriesForm.at(index);
    const selectedTypeId = group.get('categoryId')?.value;

    group.get('subCategoryId')?.reset();
    group.get('subCategoryId')?.markAsTouched(); // Ensure validation is re-run

    if (!selectedTypeId) {
      this.filteredSponsors[index] = [];
      return;
    }

    const currentSubCategoryId = this.sponsorTypeCategoriesForm.at(index).get('subCategoryId')?.value;
    this.filteredSponsors[index] = this.allSubCategories.filter(
      s => s.categoryId === selectedTypeId &&
        (s.isActive || s.id === currentSubCategoryId)
    );
  }

  get personnelArrayForm(): FormArray {
    return this.form.get('personnelList') as FormArray;
  }

  newPersonnel(): FormGroup {
    return this.fb.group({
      researcherId: [null, Validators.required],
      rolesId: [[], [Validators.required]], // at least one checked
      institutionId: [null, Validators.required],
      startDate: [null, Validators.required],
      endDate: [null]
    });

  }

  addPersonnel() {
    const index = this.personnelArrayForm.length;
    this.personnelArrayForm.push(this.newPersonnel());
    this.ResearcherSearch(index);
  }

  removePersonnel(index: number) {
    this.personnelArrayForm.removeAt(index);
    this.form.markAsDirty();
  }

  removeSponsorTypeCategory(index: number): void {
    this.sponsorTypeCategoriesForm.removeAt(index);
    this.form.markAsDirty();
  }

  addSponsorTypeCategory(): void {
    const sponsorForm = this.fb.group({
      categoryId: [null, Validators.required],
      subCategoryId: [null, Validators.required],
      sponsorProtocol: ['', Validators.maxLength(250)],
    });

    const index = this.sponsorTypeCategoriesForm.length;
    this.sponsorTypeCategoriesForm.push(sponsorForm);
    this.filteredSponsors[index] = [];
  }

  onDateChange(index: number): void {
    const personnelList = this.form.get('personnelList') as FormArray;
    const start = personnelList.at(index)?.get('startDate')?.value;
    const end = personnelList.at(index)?.get('endDate')?.value;

    let endDateControl = personnelList.at(index).get('endDate');
    if (start && end && new Date(start) > new Date(end)) {
      endDateControl?.setErrors({ dateRange: true });
    }
  }

  private ResearcherSearch(index: number): void {
    const personnelList = this.form.get('personnelList') as FormArray;
    const researcherIdControl = personnelList.at(index)?.get('researcherId') as FormControl;

    this.subs$.push(
      researcherIdControl?.valueChanges.pipe(
        switchMap(value => {
          if (value.length > 0) {
            return this.researcherService.searchResearchers(value,'ResearchPersonnel', true).pipe(tap(results => this.searchResearcher[index] = results));
          }

          this.searchResearcher[index] = [];
          return [];
        }

        )
      ).subscribe()
    );
  }
}
